import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fecha, listarOrdenes, pesos } from '../api';
import { Boton, Cargando, Error_, Pill, Tabla, Tile } from '../componentes/basicos';

/**
 * La lista de órdenes.
 *
 * **Pedido y recibido, lado a lado, siempre.** Es la comparación que hoy no
 * existe en ninguna parte, y una fila con la columna de recibido vacía sigue
 * diciendo algo: de esa orden todavía no se sabe qué aceptó el cliente.
 */
export function Ordenes(): ReactNode {
  const [parametros, setParametros] = useSearchParams();
  const [pagina, setPagina] = useState(1);

  const estado = parametros.get('estado') ?? '';
  const tipo = parametros.get('tipo') ?? '';
  const canalOrigen = parametros.get('canalOrigen') ?? '';
  const soloConIncidencias = parametros.get('revisar') === '1';

  const consulta = useQuery({
    queryKey: ['ordenes', estado, tipo, canalOrigen, soloConIncidencias, pagina],
    queryFn: () =>
      listarOrdenes({
        ...(estado ? { estado } : {}),
        ...(tipo ? { tipo } : {}),
        ...(canalOrigen ? { canalOrigen } : {}),
        ...(soloConIncidencias ? { soloConIncidencias: 'true' } : {}),
        pagina: String(pagina),
        tamano: '50',
      }),
    placeholderData: keepPreviousData,
  });

  function filtrar(clave: string, valor: string): void {
    const nuevos = new URLSearchParams(parametros);
    if (valor) nuevos.set(clave, valor);
    else nuevos.delete(clave);
    setParametros(nuevos);
    setPagina(1);
  }

  if (consulta.isLoading) return <Cargando que="las órdenes" />;
  if (consulta.isError) return <Error_>{(consulta.error as Error).message}</Error_>;

  const datos = consulta.data!;
  const porRevisar = datos.datos.filter((o) => o.incidenciasAbiertas > 0).length;
  const totalPedido = datos.datos.reduce((n, o) => n + Number(o.valorPedido), 0);

  /*
   * Solo se suma —y solo se compara— lo de las ordenes que YA tienen recepcion.
   *
   * Meter en el mismo total las que nadie ha registrado daria un faltante
   * enorme y falso: no es que no recibieran, es que no se ha mirado. Mezclar
   * las dos cosas convierte la cifra que este sistema existe para vigilar en
   * una que nadie puede creer.
   */
  const conRecepcion = datos.datos.filter((o) => o.valorRecibido !== null);
  const pedidoDeEsas = conRecepcion.reduce((n, o) => n + Number(o.valorPedido), 0);
  const recibido = conRecepcion.reduce((n, o) => n + Number(o.valorRecibido), 0);
  const faltante = pedidoDeEsas - recibido;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Órdenes</h1>
          <p className="mt-1 text-tenue">
            {datos.total} órdenes cargadas
            {porRevisar > 0 && ` · ${porRevisar} en esta página necesitan revisión`}
          </p>
        </div>
        <Link to="/cargar">
          <Boton>Cargar órdenes</Boton>
        </Link>
      </header>

      <div className="grid gap-3 sm:grid-cols-3">
        <Tile etiqueta="Órdenes" valor={String(datos.total)} />
        <Tile
          etiqueta="Pedido en esta página"
          valor={pesos(String(totalPedido))}
          pie={`${datos.datos.length} de ${datos.total}`}
        />
        <Tile
          etiqueta="Recibido"
          valor={conRecepcion.length === 0 ? '—' : pesos(String(recibido))}
          pie={
            conRecepcion.length === 0
              ? 'Ninguna orden de esta página tiene registrado lo recibido'
              : `de ${pesos(String(pedidoDeEsas))} en ${conRecepcion.length} ${
                  conRecepcion.length === 1 ? 'orden registrada' : 'órdenes registradas'
                }${faltante > 0 ? ` · faltan ${pesos(String(faltante))}` : ''}`
          }
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={estado}
          onChange={(e) => filtrar('estado', e.target.value)}
          className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
        >
          <option value="">Todos los estados</option>
          <option value="RECIBIDA">Recibidas</option>
          <option value="REQUIERE_REVISION">Por revisar</option>
          <option value="ENTREGADA">Entregadas completas</option>
          <option value="ENTREGADA_PARCIAL">Entregadas con faltante</option>
          <option value="ANULADA">Anuladas</option>
        </select>

        <select
          value={tipo}
          onChange={(e) => filtrar('tipo', e.target.value)}
          className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
        >
          <option value="">Todos los clientes</option>
          <option value="YB1">Éxito (YB1)</option>
          <option value="220">Cencosud (220)</option>
        </select>

        <select
          value={parametros.get('canalOrigen') ?? ''}
          onChange={(e) => filtrar('canalOrigen', e.target.value)}
          className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
        >
          <option value="">Los dos canales</option>
          <option value="EDI_CEN">Por EDI (CEN)</option>
          <option value="CORREO">Por correo</option>
        </select>

        <label className="flex items-center gap-2 rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm">
          <input
            type="checkbox"
            checked={soloConIncidencias}
            onChange={(e) => filtrar('revisar', e.target.checked ? '1' : '')}
          />
          Solo con algo por revisar
        </label>
      </div>

      <Tabla>
        <thead>
          <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
            <th className="px-3 py-2 font-medium">Número</th>
            <th className="px-3 py-2 font-medium">Cliente</th>
            <th className="px-3 py-2 font-medium">Documento</th>
            <th className="px-3 py-2 font-medium">Entrega</th>
            <th className="px-3 py-2 text-right font-medium">Pedido</th>
            <th className="px-3 py-2 text-right font-medium">Recibido</th>
            <th className="px-3 py-2 font-medium">Estado</th>
          </tr>
        </thead>
        <tbody>
          {datos.datos.map((o) => (
            <tr key={o.id} className="border-b border-borde last:border-0 hover:bg-fondo">
              <td className="px-3 py-2">
                <Link to={`/ordenes/${o.id}`} className="font-medium text-acento hover:underline">
                  {o.numero}
                </Link>
                {/* El canal dice cuanto puede confiar el sistema en lo que leyo. */}
                <div className="text-xs text-tenue">
                  {o.canalOrigen === 'CORREO' ? 'correo' : o.tipo}
                </div>
              </td>
              <td className="px-3 py-2">{o.clienteNombre}</td>
              <td className="px-3 py-2 whitespace-nowrap">{fecha(o.fechaDocumento)}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                {o.fechaEntregaMin === o.fechaEntregaMax ? (
                  fecha(o.fechaEntregaMin)
                ) : (
                  <span className="text-tenue">
                    {fecha(o.fechaEntregaMin)} – {fecha(o.fechaEntregaMax)}
                  </span>
                )}
              </td>
              <td className="cifra px-3 py-2 font-medium">{pesos(o.valorPedido)}</td>
              <td
                className={`cifra px-3 py-2 ${
                  o.valorRecibido !== null && Number(o.valorRecibido) < Number(o.valorPedido)
                    ? 'font-medium text-alerta'
                    : 'text-tenue'
                }`}
              >
                {pesos(o.valorRecibido)}
              </td>
              <td className="px-3 py-2">
                <Pill estado={o.estado} />
                {o.incidenciasAbiertas > 0 && (
                  <div className="mt-0.5 text-xs text-aviso">
                    {o.incidenciasAbiertas} por resolver
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Tabla>

      {datos.total > datos.tamano && (
        <div className="flex items-center justify-between">
          <Boton tipo="secundario" onClick={() => setPagina((p) => p - 1)} deshabilitado={pagina === 1}>
            Anterior
          </Boton>
          <span className="text-sm text-tenue">
            Página {datos.pagina} de {Math.ceil(datos.total / datos.tamano)}
          </span>
          <Boton
            tipo="secundario"
            onClick={() => setPagina((p) => p + 1)}
            deshabilitado={pagina >= Math.ceil(datos.total / datos.tamano)}
          >
            Siguiente
          </Boton>
        </div>
      )}
    </div>
  );
}
